import { Link } from "react-router-dom";
import { ArrowRight, BookOpenCheck, Check, FileStack, Layers, Lock, Mail, Sparkles } from "lucide-react";
import { B } from "../design/tokens";

const features = [
  {
    icon: Mail,
    title: "Gmail, Outlook or any IMAP inbox",
    body: "Connect a mailbox once. New mail is checked every few minutes with read-only access. No forwarding rules, no plugins.",
  },
  {
    icon: Sparkles,
    title: "Parsed by AI, not templates",
    body: "Email bodies, PDFs and scanned images are read together. Vendor, totals, tax, dates and card are extracted from any layout.",
  },
  {
    icon: Layers,
    title: "Paid, owed or received",
    body: "Every document is classified as a receipt, a bill or income, and duplicates are caught before they reach your books.",
  },
  {
    icon: BookOpenCheck,
    title: "Straight into QuickBooks",
    body: "Receipts post as Purchases, bills as Bills and income as Sales Receipts, with the source document attached.",
  },
  {
    icon: FileStack,
    title: "Paper receipts too",
    body: "Drop a photo or a multi-page PDF scan. Each receipt inside is split out and processed individually.",
  },
  {
    icon: Lock,
    title: "Secure by design",
    body: "Gmail and Outlook connect over OAuth, so your password is never shared. Tokens and credentials are encrypted at rest.",
  },
];

const PREVIEW_ROWS = [
  { mark: "AW", tint: B.tints.peach, vendor: "Amazon Web Services", meta: "Invoice INV-20931 · Oct 28", amount: "$1,284.19", type: "Bill" },
  { mark: "DL", tint: B.tints.sky, vendor: "Delta Air Lines", meta: "Receipt · Visa •••• 4242 · Oct 26", amount: "$612.40", type: "Receipt" },
  { mark: "AC", tint: B.tints.mint, vendor: "Acme Consulting LLC", meta: "Payment received · Oct 24", amount: "$4,500.00", type: "Income" },
  { mark: "FG", tint: B.tints.lavender, vendor: "Figma", meta: "Subscription · Amex •••• 1008 · Oct 21", amount: "$45.00", type: "Receipt" },
];

function ProductPreview() {
  return (
    <div className="preview" aria-hidden="true">
      <div className="preview-bar">
        <i />
        <i />
        <i />
        <span>Receipts · synced from 2 inboxes</span>
      </div>
      {PREVIEW_ROWS.map((r) => (
        <div key={r.vendor} className="preview-row">
          <span className="preview-logo" style={{ background: r.tint.bg, color: r.tint.ink }}>
            {r.mark}
          </span>
          <div style={{ minWidth: 0 }}>
            <div className="preview-vendor">{r.vendor}</div>
            <div className="preview-meta">{r.meta}</div>
          </div>
          <span className="preview-amount">{r.amount}</span>
          <span className="badge reviewed" style={{ gap: 4 }}>
            <Check size={12} /> {r.type}
          </span>
        </div>
      ))}
    </div>
  );
}

export default function Landing() {
  return (
    <>
      <div className="hero-wrap">
        <section className="hero">
          <div className="hero-inner">
            <div className="hero-eyebrow">
              <b>New</b> AI extraction for PDFs and scans
            </div>
            <h1 className="hero-title">
              Your receipts, <span className="accent">reconciled</span> before you open your inbox.
            </h1>
            <p className="hero-sub">
              Set &amp; Ledger finds every receipt, invoice and payment in your mailbox, extracts the details with AI, and
              posts them to QuickBooks Online.
            </p>
            <div className="hero-cta">
              <Link to="/register" className="btn btn-primary btn-lg">
                Get started <ArrowRight size={16} />
              </Link>
              <Link to="/how-it-works" className="btn btn-ghost btn-lg">
                See how it works
              </Link>
            </div>
            <p className="hero-note">
              <span>
                <Check size={14} color={B.accent} /> Read-only inbox access
              </span>
              <span>
                <Check size={14} color={B.accent} /> Set up in minutes
              </span>
            </p>
          </div>
          <div className="hero-art">
            <ProductPreview />
          </div>
        </section>
      </div>

      <section className="section">
        <div className="section-eyebrow">Features</div>
        <h2 className="section-heading">Bookkeeping that keeps itself current</h2>
        <p className="section-lead">
          From inbox to ledger without copy and paste. Review what was captured, correct anything by hand, and sync with a
          click or automatically.
        </p>
        <div className="feature-grid">
          {features.map((f) => (
            <div key={f.title} className="feature-card">
              <div className="feature-icon">
                <f.icon size={20} />
              </div>
              <h3>{f.title}</h3>
              <p>{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="cta-band">
        <div className="cta-band-inner">
          <div>
            <h2>Close the books faster.</h2>
            <p>Connect your inbox and let the receipts file themselves.</p>
          </div>
          <Link to="/register" className="btn btn-primary btn-lg">
            Create your account <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </>
  );
}
